import React, { useMemo, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import RouteSeo from '../components/RouteSeo';
import ToolContent from '../components/ToolContent';
import ToolLayout from '../components/ToolLayout';
import { REGEX_ABOUT } from '../content/toolGuides';
import { REGEX_ROUTE } from '../content/tools';
import { MAX_MATCHES, RegexError, REGEX_FLAGS, runRegex } from '../utils/regex';
import './RegexTester.css';

const EXAMPLE_PATTERN = '(?<user>\\w+)@(?<host>[\\w.]+)';
const EXAMPLE_SUBJECT = 'ana@example.com, bo@test.com and carol@sub.domain.org';

const RegexTester: React.FC = () => {
  const [pattern, setPattern] = useState('');
  const [flags, setFlags] = useState('g');
  const [subject, setSubject] = useState('');
  const [replacement, setReplacement] = useState('');
  const [showReplace, setShowReplace] = useState(false);

  const { result, error } = useMemo(() => {
    try {
      return {
        result: runRegex(pattern, flags, subject, showReplace ? replacement : undefined),
        error: null,
      };
    } catch (err) {
      return {
        result: null,
        error: err instanceof RegexError ? `Invalid pattern: ${err.message}` : 'Could not run that pattern.',
      };
    }
  }, [pattern, flags, subject, replacement, showReplace]);

  const toggleFlag = (flag: string) => {
    setFlags((current) =>
      current.includes(flag) ? current.replace(flag, '') : `${current}${flag}`,
    );
  };

  const loadExample = () => {
    setPattern(EXAMPLE_PATTERN);
    setSubject(EXAMPLE_SUBJECT);
    setFlags('g');
  };

  const matches = result?.matches ?? [];

  return (
    <>
      <RouteSeo route={REGEX_ROUTE} />
      <ToolLayout
        title="Regex Tester"
        description="Test a regular expression against sample text, with live match highlighting"
        icon={faMagnifyingGlass}
        controls={
          <>
            <div className="tool-controls__group regex-flags" role="group" aria-label="Regex flags">
              {REGEX_FLAGS.map(({ flag, label, hint }) => (
                <Form.Check
                  key={flag}
                  type="checkbox"
                  id={`regex-flag-${flag}`}
                  label={`${flag} — ${label}`}
                  title={hint}
                  checked={flags.includes(flag)}
                  onChange={() => toggleFlag(flag)}
                  inline
                />
              ))}
            </div>
            <div className="tool-controls__group">
              <Button variant="link" size="sm" className="px-0" onClick={loadExample}>
                Load Example
              </Button>
            </div>
          </>
        }
        error={error}
        inputContent={
          <Form className="regex-input">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="regex-pattern">
                <strong>Pattern</strong>
              </Form.Label>
              <InputGroup>
                <InputGroup.Text>/</InputGroup.Text>
                <Form.Control
                  id="regex-pattern"
                  value={pattern}
                  onChange={(event) => setPattern(event.target.value)}
                  placeholder="(\w+)@(\w+)\.com"
                  className="font-monospace"
                  spellCheck={false}
                />
                <InputGroup.Text>/{flags}</InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="regex-subject">
                <strong>Test string</strong>
              </Form.Label>
              <Form.Control
                id="regex-subject"
                as="textarea"
                rows={10}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="Paste the text to match against..."
                className="font-monospace"
                spellCheck={false}
              />
            </Form.Group>

            <Form.Check
              type="checkbox"
              id="regex-show-replace"
              label="Preview a replacement"
              checked={showReplace}
              onChange={(event) => setShowReplace(event.target.checked)}
            />
            {showReplace && (
              <Form.Group className="mt-2">
                <Form.Label htmlFor="regex-replacement" className="visually-hidden">
                  Replacement
                </Form.Label>
                <Form.Control
                  id="regex-replacement"
                  value={replacement}
                  onChange={(event) => setReplacement(event.target.value)}
                  placeholder="$1 or $<name>"
                  className="font-monospace"
                  spellCheck={false}
                />
                <Form.Text className="text-muted">
                  Use <code>$1</code> for a numbered group and <code>$&lt;name&gt;</code> for a named
                  one.
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        }
        outputContent={
          <div className="regex-result">
            <h3 className="regex-result__heading">
              {pattern && subject
                ? `${matches.length}${result?.truncated ? '+' : ''} ${matches.length === 1 ? 'match' : 'matches'}`
                : 'Matches'}
            </h3>

            {result?.truncated && (
              <p className="regex-result__note">
                Stopped after {MAX_MATCHES} matches — narrow the pattern to see the rest.
              </p>
            )}

            {!pattern || !subject ? (
              <p className="text-muted mb-0">
                Enter a pattern and some test text to see matches highlighted here.
              </p>
            ) : (
              <>
                <div className="regex-highlight" aria-label="Test string with matches highlighted">
                  {result?.segments.length ? (
                    result.segments.map((segment, index) =>
                      segment.matchOrdinal === null ? (
                        <span key={index}>{segment.text}</span>
                      ) : (
                        <mark className="regex-highlight__hit" key={index}>
                          {segment.text}
                        </mark>
                      ),
                    )
                  ) : (
                    <span className="text-muted">No text to match against.</span>
                  )}
                </div>

                {matches.length === 0 ? (
                  <p className="text-muted mb-0">No matches.</p>
                ) : (
                  <ol className="regex-matches">
                    {matches.map((match) => (
                      <li className="regex-matches__item" key={match.ordinal}>
                        <code className="regex-matches__value">
                          {match.value === '' ? '(empty match)' : match.value}
                        </code>
                        <span className="regex-matches__meta">at index {match.index}</span>
                        {match.groups.length > 0 && (
                          <ul className="regex-groups">
                            {match.groups.map((group, index) => (
                              <li key={index}>
                                <span className="regex-groups__name">${index + 1}</span>
                                <code>{group === undefined ? '(not matched)' : group}</code>
                              </li>
                            ))}
                          </ul>
                        )}
                        {Object.keys(match.namedGroups).length > 0 && (
                          <ul className="regex-groups">
                            {Object.entries(match.namedGroups).map(([name, value]) => (
                              <li key={name}>
                                <span className="regex-groups__name">{name}</span>
                                <code>{value === undefined ? '(not matched)' : value}</code>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ol>
                )}

                {showReplace && result?.replaced !== undefined && (
                  <section className="regex-replaced">
                    <h3 className="regex-result__heading">After replace</h3>
                    <pre className="code-block">
                      <code>{result.replaced}</code>
                    </pre>
                  </section>
                )}
              </>
            )}
          </div>
        }
        aboutTitle="About Regular Expressions"
        about={REGEX_ABOUT}
      >
        <ToolContent route={REGEX_ROUTE} />
      </ToolLayout>
    </>
  );
};

export default RegexTester;
